<template>
  <div :id="chartData._id" class="jzliu-bi-engine-chart-warp" :style="{ height: warpHeight }"></div>
</template>

<script>
import { Column } from '@antv/g2plot'
import { calcDescartes, mateMapping } from '../../utils'
import { getCategoryDimension, hexToRgba } from '../../utils/index'
import { customKey } from '../../utils/enum.data'
import { initHeight } from '../../utils/index'
import { axisOffset } from '../../utils/panelConf'
import { getSeriesFieldValues } from '../../utils/utils'

export default {
  name: 'column',
  props: {
    chartData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    chartConfigHandler: {
      type: Function,
      default: (conf) => conf,
    },
  },
  data() {
    return {
      renderTimer: null,
      warpHeight: 200,
      stackedColumnPlot: null,
    }
  },
  watch: {
    'chartData.styleConfig.helperData.fieldMap': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.columnBackgroundOpacity': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.gradientEffect': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.columnWidthRatio': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.labelConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.legendConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      initHeight.call(this, 'warpHeight')
      this.renderChart()
    })
  },
  methods: {
    // 防抖渲染图表
    debounceRenderChart() {
      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
      }
      this.renderTimer = setTimeout(() => {
        this.stackedColumnPlot?.destroy()
        this.renderChart()
      }, 300) // 300ms 防抖延迟
    },

    renderChart() {
      const {
        _id: id,
        data: {
          data: chartData,
          meta,
        },
      } = this.chartData
      const { rows, values, columns } = getCategoryDimension(this.chartData.fields)
      const mateMap = mateMapping(meta)
      const labelConfig = this.chartData.styleConfig.commongConfig.labelConfig
      const legendConfig = this.chartData.styleConfig.commongConfig.legendConfig
      const conf = {
        xField: 'xField',
        yField: 'yField',
        xAxis:{
          title: {
            text: rows.map(v => this.chartData.styleConfig.helperData.nameMap[v]).join('-') || false,
            style: {
              fill: '#86909C', // 单位文字颜色
              fontSize: 12, // 单位文字字体大小
              lineHeight: 20, // 行高+8 (12 + 8 = 20)
            }
          },
          label: {
            style: {
              fill: '#86909C', // 刻度字体颜色
              fontSize: 12, // 刻度字体大小
              lineHeight: 20, // 行高+8，x轴y轴行高统一
            }
          },
          line: {
            style: {
              stroke: '#EDEFF2', // 轴、刻度颜色
              lineWidth: 1, // 1px
            }
          },
          tickLine: {
            style: {
              stroke: '#EDEFF2', // 刻度线颜色
              lineWidth: 1, // 1px
            }
          }
        },
        yAxis: {
          title: false, // 不显示 Y 轴标题
          label: {
            style: {
              fill: '#86909C', // 刻度字体颜色
              fontSize: 12, // 刻度字体大小
              lineHeight: 20, // 行高+8，x轴y轴行高统一
            }
          },
          line: false, // 不显示 Y 轴线
          tickLine: false, // 不显示 Y 轴刻度线
          grid: {
            line: {
              style: {
                stroke: '#EDEFF2', // 网格线颜色
                lineWidth: 1, // 1px
                lineDash: [2, 2], // 虚线样式：2px 实线，2px 空白
              }
            }
          }
        },
        seriesField: 'seriesField',
        isGroup: true,
        columnWidthRatio: this.chartData.styleConfig.commongConfig.columnWidthRatio / 100,
        maxColumnWidth: this.chartData.styleConfig.commongConfig.maxColumnWidth || null,
        label: labelConfig.show ? {
          // G2Plot v2 版本的 label 配置
          autoHide: !labelConfig.showAll,
          position: labelConfig.position === '' ? 'middle' : labelConfig.position,
          layout: [
            ...(labelConfig.position === '' ? [{ type: 'interval-adjust-position' }] : []),
            ...(labelConfig.showAll? [] : [{ type: 'interval-hide-overlap' }]),
          ],
          style: {
            fontSize: labelConfig.textConfig.fontSize,
            fill: labelConfig.textConfig.color,
            fontWeight: labelConfig.textConfig.isBold ? 'bold' : 'normal',
            fontStyle: labelConfig.textConfig.isItalic ? 'italic' : 'normal',
          }
        } : false,
        color: (d) => {
          const baseColor = this.chartData.styleConfig.helperData.fieldMap[d.seriesField]?.[customKey + 'seriesColor']
          if (!this.chartData.styleConfig.commongConfig.gradientEffect) {
            return hexToRgba(baseColor, this.chartData.styleConfig.commongConfig.columnBackgroundOpacity / 100)
          }
          if (!baseColor) return '#1890ff'

          // 使用配置的透明度
          const opacity = this.chartData.styleConfig.commongConfig.columnBackgroundOpacity / 100

          // 生成渐变：从白色到基础颜色，中间色使用配置的透明度
          return `l(270) 0:#ffffff 0.5:${hexToRgba(baseColor, opacity)} 1:${baseColor}`
        },
        legend: legendConfig.show ? {
          position: legendConfig.position,
          marker: (text, index, item) => {
            return {
              symbol: 'circle', // 使用圆形标记
              style: {
                fill: this.chartData.styleConfig.helperData.fieldMap[item.name]?.[customKey + 'seriesColor'],
              },
            };
          },
          itemName: {
            style: {
              fill: '#4E5969', // 图例文字颜色
              fontSize: 12, // 字体大小
              lineHeight: 20, // 行高+8 (12 + 8 = 20)
            }
          }
        } : false,
      }
      let dataResult = []
      chartData.forEach((v) => {
        /***
         * 这里需要考虑两个逻辑
         1、如果是没有选择颜色维度 则需要创建[度量][维度（多个需要拼接）]笛卡尔乘积 以度量作为seriesField
         2、如果选择颜色维度  度量取第一个 并且以颜色维度作为seriesField
         ***/
        // 判断是否拥有颜色维度
        const hasColorDimensionFlag = !!columns.length
        let _excludeValues = []
        let _includeValues = []
        const lists = [_excludeValues, _includeValues]
        // 虚拟数据
         const opt = {}
        for (let i in v) {
          opt[i] = v[i]
        }
        const orderedFields = rows.reduce((acc, rowKey) => {
          if (Object.prototype.hasOwnProperty.call(v, rowKey)) {
            acc.push(v[rowKey])
          }
          return acc
        }, [])
        const joinedFields = orderedFields.join('-')
        for (let i in v) {
          if (values.includes(i)) {
            _excludeValues.push({ [i]: v[i] })
          }
          if (rows.includes(i)) {
            _includeValues.push({ xField: joinedFields })
          }
          if (hasColorDimensionFlag) {
            v.xField = joinedFields
          }
        }
        // 没有选择颜色维度
        if (!hasColorDimensionFlag) {
          dataResult.push(
            ...calcDescartes(lists).map((cartesianItem) => {
              // 先复制原始数据 v 的所有属性，保留所有原始数据
              const obj = { ...v }
              
              // 然后覆盖笛卡尔积产生的特定字段
              cartesianItem.forEach((c) => {
                for (let i in c) {
                  obj[i] = c[i]
                  if (values.includes(i)) {
                    obj.seriesField = mateMap.get(i)
                    obj.yField = c[i] ?? 0
                  }
                }
              })
              return obj
            })
          )
        } else {
          v.yField = v[values[0]] ?? 0
          v.seriesField = v[columns[0]]
          dataResult.push(v)
        }
      })
      const confOpt = this.chartData.isCancel ? conf : this.chartConfigHandler(conf, this.chartData)
      this.stackedColumnPlot = new Column(id, {
        ...confOpt,
        data: dataResult,
        meta: {
          ...getSeriesFieldValues(this.chartData)
        },
      })
      this.stackedColumnPlot.render()
      this.stackedColumnPlot.on('element:click', (res) => {
        this.$emit('drill-item-click', res.data)
      });
    },
    destroyedChart() {
      this.stackedColumnPlot?.destroy()
      this.stackedColumnPlot.off('element:click')
    }
  },
}
</script>

<style scoped lang="less"></style>
